import { Card, CardActionArea, Typography } from '@material-ui/core';
import {
  createStyles,
  fade,
  makeStyles,
  Theme
} from '@material-ui/core/styles';
import {
  RadioButtonCheckedRounded,
  RadioButtonUncheckedRounded
} from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';
import { Dispatch, SetStateAction } from 'react';
import ChartStatus from './ChartStatus';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    statusContainer: {
      display: 'flex',
      '& > *': {
        width: '33.33%',
        marginRight: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
          marginRight: 0,
          marginBottom: theme.spacing(1),
          width: '100%'
        }
      },
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column'
      }
    },
    statusCard: {
      border: '2px solid #cccccc55',
      borderRadius: theme.spacing(2),
      transition: '0.4s',
      '&:hover, &.Mui-focusVisible': {
        borderColor: theme.palette.primary.main
      },
      cursor: 'pointer',
      '&:last-child': {
        marginRight: 0,
        [theme.breakpoints.down('xs')]: {
          marginBottom: 0
        }
      }
    },
    selectedObject: {
      borderColor: theme.palette.primary.main,
      backgroundColor: fade(theme.palette.primary.main, 0.1)
    },
    statusCardAction: {
      padding: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1, 2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }
    },
    breakAllWord: {
      wordBreak: 'break-all'
    }
  })
);

export interface Status {
  id: number;
  name: string;
  label: string;
  quantity: number;
  chart: {
    labels: string[];
    data: number[];
  };
}

interface ChartDocRevisionStatusProps {
  renderStatuses: Status[];
  selectedStatus: Status | null;
  setSelectedStatus: Dispatch<SetStateAction<Status | null>>;
  loadDocumentRevisionData: (status: Status | null) => void;
  loading?: boolean;
}

export default function ChartDocRevisionStatus({
  renderStatuses,
  selectedStatus,
  setSelectedStatus,
  loadDocumentRevisionData,
  loading = false
}: ChartDocRevisionStatusProps) {
  const classes = useStyles();

  function onClickStatusCard(status: Status) {
    const newStatus = status.label === selectedStatus?.label ? null : status;
    setSelectedStatus(newStatus);
    loadDocumentRevisionData(newStatus);
  }

  return (
    <div>
      {loading ? (
        <Skeleton
          animation="wave"
          variant="rect"
          height={242}
          style={{ borderRadius: 8 }}
        />
      ) : (
        <div className={classes.statusContainer}>
          {renderStatuses.map((status) => {
            const isSelected = status.label === selectedStatus?.label;
            const icon = isSelected ? (
              <RadioButtonCheckedRounded color="primary" />
            ) : (
              <RadioButtonUncheckedRounded color="action" />
            );
            return (
              <Card
                className={clsx(classes.statusCard, {
                  [classes.selectedObject]: isSelected
                })}
                variant="outlined"
                key={status.id}
              >
                <CardActionArea
                  className={classes.statusCardAction}
                  onClick={() => onClickStatusCard(status)}
                >
                  <div>
                    <Typography
                      className={classes.breakAllWord}
                      color="textSecondary"
                    >
                      {status.label}
                    </Typography>
                    <Typography variant="h4">{status.quantity}</Typography>
                  </div>
                  <ChartStatus
                    labels={status.chart.labels}
                    quantities={status.chart.data}
                    icon={icon}
                  />
                </CardActionArea>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
